import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { CreateRecoverySetPayload } from '../api'

interface CreateRecoverySetDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSubmit: (payload: CreateRecoverySetPayload) => void
    isSubmitting?: boolean
}

const NAME_MAX_LENGTH = 45
const INFO_MAX_LENGTH = 1000

export function CreateRecoverySetDialog({
    open,
    onOpenChange,
    onSubmit,
    isSubmitting = false,
}: CreateRecoverySetDialogProps) {
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [recoveryInformation, setRecoveryInformation] = useState('')

    useEffect(() => {
        if (!open) {
            setName('')
            setRecoveryInformation('')
        }
    }, [open])

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault()
            const trimmed = name.trim()
            if (!trimmed) return
            onSubmit({ name: trimmed.slice(0, NAME_MAX_LENGTH), recoveryInformation: recoveryInformation.trim() || undefined })
        },
        [name, recoveryInformation, onSubmit]
    )

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next && !isSubmitting) {
                setName('')
                setRecoveryInformation('')
            }
            onOpenChange(next)
        },
        [onOpenChange, isSubmitting]
    )

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('recoverRequests.createRecoverySet', 'Create Recovery Set')}</DialogTitle>
                    <DialogDescription>
                        {t('recoverRequests.createRecoverySetDescription', 'Add a new recovery set to group request IDs and run the mobilisation workflow.')}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="recovery-set-name">
                            {t('recoverRequests.setName', 'Set name')} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="recovery-set-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('recoverRequests.setNamePlaceholder', 'e.g. DR Exercise Dec 2024')}
                            maxLength={NAME_MAX_LENGTH}
                            required
                            disabled={isSubmitting}
                            autoFocus
                        />
                        <p className="text-xs text-muted-foreground">
                            {name.length}/{NAME_MAX_LENGTH}
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="recovery-set-info">{t('recoverRequests.recoveryInformation', 'Recovery information')}</Label>
                        <textarea
                            id="recovery-set-info"
                            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
                            value={recoveryInformation}
                            onChange={(e) => setRecoveryInformation(e.target.value)}
                            placeholder={t('recoverRequests.recoveryInformationPlaceholder', 'Optional notes or context')}
                            maxLength={INFO_MAX_LENGTH}
                            disabled={isSubmitting}
                            rows={3}
                        />
                        <p className="text-xs text-muted-foreground">
                            {recoveryInformation.length}/{INFO_MAX_LENGTH}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={!name.trim() || isSubmitting}>
                            {isSubmitting ? t('common.creating', 'Creating…') : t('recoverRequests.createSet', 'Create set')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
